import { useState, useEffect } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  BookOpen,
  ClipboardCheck,
  Loader2,
  Play,
  AlertCircle,
  ListOrdered,
  Shuffle,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useCategories } from "@/hooks/use-questions";
import { useExam } from "@/hooks/use-exams";
import { useCreateTest } from "@/hooks/use-tests";
import { getRandomQuestions, getQuestions } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";
import type { Question } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CategorySelect } from "@/components/category-select";

const SET_SIZE = 10;

export const Route = createFileRoute("/_main/exams/$slug/test/new")({
  component: NewTestPage,
});

function buildSequentialSets(total: number, size: number) {
  const sets: { start: number; end: number; label: string }[] = [];
  for (let i = 1; i <= total; i += size) {
    const end = Math.min(i + size - 1, total);
    sets.push({ start: i, end, label: `${i}~${end}` });
  }
  return sets;
}

export function NewTestPage() {
  const params = useParams({ strict: false }) as { slug: string };
  const examSlug = params.slug;
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { data: categories } = useCategories(examSlug);
  const { data: exam, isLoading: examLoading } = useExam(examSlug);
  const createTestMutation = useCreateTest(examSlug);

  const QUESTION_COUNTS = [
    { value: 10, label: `10${t("common.questions")}` },
    { value: 20, label: `20${t("common.questions")}` },
    { value: 30, label: `30${t("common.questions")}` },
    { value: 100, label: `100${t("common.questions")}` },
  ];

  const [testType, setTestType] = useState<"random" | "sequential">("random");
  const [mode, setMode] = useState<"practice" | "exam">("practice");
  const [questionCount, setQuestionCount] = useState(10);
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [selectedSet, setSelectedSet] = useState(0);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seqProgress, setSeqProgress] = useState<
    Record<string, { score: number; total: number; date: string }>
  >({});

  const totalQuestions = exam?.question_count ?? 0;
  const sequentialSets = buildSequentialSets(totalQuestions, SET_SIZE);

  // Load sequential progress from localStorage (namespaced by exam)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`sequential_progress_${examSlug}`);
      if (raw) setSeqProgress(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, [examSlug]);

  async function handleStartTest() {
    setStarting(true);
    setError(null);

    try {
      let questions: Question[];

      if (testType === "sequential") {
        const set = sequentialSets[selectedSet];
        // page is 1-indexed: set.start=1 -> page=1, set.start=11 -> page=2, etc.
        const page = Math.floor((set.start - 1) / SET_SIZE) + 1;
        const result = await getQuestions(examSlug, page, SET_SIZE);
        questions = result.questions;
      } else {
        const count = questionCount;
        questions = await getRandomQuestions(examSlug, count, categoryId);
      }

      if (questions.length === 0) {
        setError("선택한 조건에 해당하는 문제가 없습니다.");
        setStarting(false);
        return;
      }

      const questionIds = questions.map((q) => q.id);

      // Create test session
      const result = await createTestMutation.mutateAsync({
        question_ids: questionIds,
        mode: mode === "practice" ? "practice" : "exam",
      });

      // Shuffle questions to prevent spatial memorization (Fisher-Yates)
      const shuffled = [...result.questions];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      // Store questions and session in localStorage for the test page
      const testData = {
        session: result.session,
        questions: shuffled,
      };
      localStorage.setItem(`test_${result.session.id}`, JSON.stringify(testData));

      // Tag sequential test for progress tracking
      if (testType === "sequential") {
        localStorage.setItem(
          `test_meta_${result.session.id}`,
          JSON.stringify({ type: "sequential", setIndex: selectedSet, examSlug }),
        );
      }

      // Redirect to test page
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: result.session.id },
      });
    } catch (err) {
      setError(createErrorMessage(err, "시험을 시작하는 중 오류가 발생했습니다."));
      setStarting(false);
    }
  }

  if (authLoading || examLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
            {t("test.new_title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {exam?.name ? `${exam.name} - ` : ""}{t("test.new_desc")}
          </p>
        </div>

        {/* Test Type Selection */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-zinc-700 dark:text-zinc-300">
            {t("test.question_method")}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Card
              className={`cursor-pointer transition-all ${
                testType === "random"
                  ? "border-violet-500 ring-2 ring-violet-200 dark:border-violet-400 dark:ring-violet-900"
                  : "hover:border-zinc-300 dark:hover:border-zinc-600"
              }`}
              onClick={() => setTestType("random")}
            >
              <CardContent className="flex items-center gap-3 py-4">
                <Shuffle
                  className={`h-5 w-5 ${
                    testType === "random" ? "text-violet-500" : "text-zinc-400"
                  }`}
                />
                <div>
                  <p className="text-sm font-semibold">{t("test.random")}</p>
                  <p className="text-xs text-muted-foreground">{t("test.random_desc")}</p>
                </div>
              </CardContent>
            </Card>
            <Card
              className={`cursor-pointer transition-all ${
                testType === "sequential"
                  ? "border-violet-500 ring-2 ring-violet-200 dark:border-violet-400 dark:ring-violet-900"
                  : "hover:border-zinc-300 dark:hover:border-zinc-600"
              }`}
              onClick={() => setTestType("sequential")}
            >
              <CardContent className="flex items-center gap-3 py-4">
                <ListOrdered
                  className={`h-5 w-5 ${
                    testType === "sequential" ? "text-violet-500" : "text-zinc-400"
                  }`}
                />
                <div>
                  <p className="text-sm font-semibold">{t("test.sequential")}</p>
                  <p className="text-xs text-muted-foreground">{t("test.sequential_desc")}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-zinc-700 dark:text-zinc-300">
            {t("test.mode")}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* Practice Mode Card */}
            <Card
              className={`cursor-pointer transition-all ${
                mode === "practice"
                  ? "border-blue-500 ring-2 ring-blue-200 dark:border-blue-400 dark:ring-blue-900"
                  : "hover:border-zinc-300 dark:hover:border-zinc-600"
              }`}
              onClick={() => setMode("practice")}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <BookOpen
                    className={`h-5 w-5 ${
                      mode === "practice"
                        ? "text-blue-500"
                        : "text-zinc-400"
                    }`}
                  />
                  <CardTitle className="text-base">{t("test.practice_mode")}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm">
                  {t("test.practice_mode_desc")}
                </CardDescription>
              </CardContent>
            </Card>

            {/* Exam Mode Card */}
            <Card
              className={`cursor-pointer transition-all ${
                mode === "exam"
                  ? "border-orange-500 ring-2 ring-orange-200 dark:border-orange-400 dark:ring-orange-900"
                  : "hover:border-zinc-300 dark:hover:border-zinc-600"
              }`}
              onClick={() => setMode("exam")}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <ClipboardCheck
                    className={`h-5 w-5 ${
                      mode === "exam"
                        ? "text-orange-500"
                        : "text-zinc-400"
                    }`}
                  />
                  <CardTitle className="text-base">{t("test.exam_mode")}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm">
                  {t("test.exam_mode_desc")}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Sequential Set Selection */}
        {testType === "sequential" && sequentialSets.length > 0 && (
          <div className="mb-6">
            <h2 className="mb-3 text-sm font-semibold text-zinc-700 dark:text-zinc-300">
              {t("test.question_range")}
            </h2>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {sequentialSets.map((set, idx) => {
                const prog = seqProgress[String(idx)];
                const isCompleted = !!prog;
                const pct = prog ? Math.round((prog.score / prog.total) * 100) : 0;

                return (
                  <button
                    key={set.start}
                    type="button"
                    onClick={() => setSelectedSet(idx)}
                    className={`relative flex h-14 flex-col items-center justify-center rounded-lg border px-3 py-2 text-sm font-semibold transition-all ${
                      selectedSet === idx
                        ? "border-primary bg-primary text-primary-foreground"
                        : isCompleted
                          ? "border-green-300 bg-green-50 text-green-700 hover:border-green-400 dark:border-green-700 dark:bg-green-950 dark:text-green-400"
                          : "border-input bg-background text-foreground hover:bg-accent"
                    }`}
                  >
                    <span>Q{set.label}</span>
                    {isCompleted && selectedSet !== idx && (
                      <span className="mt-0.5 text-[11px] font-bold leading-none">
                        {pct}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Overall progress */}
            {Object.keys(seqProgress).length > 0 && (
              <div className="mt-3 flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400">
                <span>
                  {t("test.progress")}: {Object.keys(seqProgress).length}/{sequentialSets.length} {t("test.range_complete")}
                </span>
                <span>
                  {t("test.average")}:{" "}
                  {Math.round(
                    Object.values(seqProgress).reduce(
                      (sum, p) => sum + (p.score / p.total) * 100,
                      0,
                    ) / Object.keys(seqProgress).length,
                  )}
                  %
                </span>
              </div>
            )}
          </div>
        )}

        {/* Random Options */}
        {testType === "random" && (
          <>
            {/* Question Count */}
            <div className="mb-6">
              <h2 className="mb-3 text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                {t("test.question_count")}
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {QUESTION_COUNTS.map((option) => (
                  <Button
                    key={option.value}
                    size="lg"
                    variant={questionCount === option.value ? "default" : "outline"}
                    className="h-12 w-full justify-center text-base font-semibold"
                    onClick={() => setQuestionCount(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div className="mb-8">
              <h2 className="mb-3 text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                {t("test.category_optional")}
              </h2>
              <CategorySelect
                categories={categories}
                value={categoryId ? String(categoryId) : "all"}
                onValueChange={(value) =>
                  setCategoryId(value === "all" ? undefined : Number(value))
                }
                allLabel={t("questions.all_categories")}
                triggerClassName="w-full"
              />
            </div>
          </>
        )}

        {/* Summary */}
        <Card className="mb-6">
          <CardContent className="flex flex-wrap items-center gap-2 py-4">
            <Badge
              variant={mode === "practice" ? "default" : "secondary"}
              className="text-sm"
            >
              {mode === "practice" ? t("test.practice_mode") : t("test.exam_mode")}
            </Badge>
            {testType === "sequential" && sequentialSets.length > 0 ? (
              <Badge variant="outline" className="text-sm">
                Q{sequentialSets[selectedSet].label}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-sm">
                {`${questionCount}${t("common.questions")} (${t("test.random")})`}
              </Badge>
            )}
            {testType === "random" && categoryId && categories && (
              <Badge variant="outline" className="text-sm">
                {categories.find((c) => c.id === categoryId)?.name}
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Start Button */}
        <Button
          className="h-14 w-full min-h-[48px] text-lg font-semibold"
          size="lg"
          disabled={starting}
          onClick={handleStartTest}
        >
          {starting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {t("test.preparing")}
            </>
          ) : (
            <>
              <Play className="mr-2 h-5 w-5" />
              {t("test.start_exam")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
